import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Code2, Workflow, FlaskConical, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useLang, BLOG_INFO } from "../context/LangContext";
import useDocumentTitle from "../hooks/useDocumentTitle";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BlogBadge from "../components/BlogBadge";

const DESC = {
  suica: {
    pt: "Vida na Suíça, burocracia, imigração, vistos, seguros de saúde e custo de vida — guias práticos para quem quer viver e trabalhar no país.",
    en: "Life in Switzerland, bureaucracy, immigration, visas, health insurance and cost of living — practical guides for living and working there.",
  },
  desenvolvimento: {
    pt: "Desenvolvimento de software: Java, Spring Boot, Supabase e React Native. Tutoriais passo a passo e boas práticas de engenharia.",
    en: "Software development: Java, Spring Boot, Supabase and React Native. Step-by-step tutorials and engineering best practices.",
  },
  automacao: {
    pt: "Automação e produtividade: n8n, pipelines CI/CD, cron jobs e fluxos que poupam horas de trabalho manual.",
    en: "Automation and productivity: n8n, CI/CD pipelines, cron jobs and workflows that save hours of manual work.",
  },
};

const STEPS = [
  { icon: BookOpen, pt: ["Guias práticos", "Passo a passo claros sobre burocracia, processos e o dia a dia real."], en: ["Practical guides", "Clear step-by-step guides on bureaucracy, processes and real everyday life."] },
  { icon: Code2, pt: ["Tutoriais técnicos", "Exemplos de código e integrações em Java, Spring Boot, Supabase e React Native."], en: ["Technical tutorials", "Code examples and integrations in Java, Spring Boot, Supabase and React Native."] },
  { icon: Workflow, pt: ["Automações", "Fluxos com n8n, pipelines CI/CD e cron jobs que poupam horas de trabalho."], en: ["Automations", "n8n flows, CI/CD pipelines and cron jobs that save hours of work."] },
  { icon: FlaskConical, pt: ["Experiências reais", "Notas e resultados dos meus próprios testes e projetos."], en: ["Real experiments", "Notes and results from my own tests and projects."] },
];

export default function AboutPage() {
  const { t, lang } = useLang();
  const [blogs, setBlogs] = useState([]);
  useDocumentTitle(
    t("Sobre · ReiMultiBlog", "About · ReiMultiBlog"),
    t("Conhece a ReiMultiBlog e a missão dos três blogs: Suíça, Desenvolvimento e Automação.", "Meet ReiMultiBlog and the mission behind its three blogs: Swiss life, Development and Automation.")
  );

  useEffect(() => { api.get("/public/blogs").then((r) => setBlogs(r.data)).catch(() => {}); }, []);

  return (
    <div className="App relative grain">
      <Navbar />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 text-center relative z-10">
        <img src="/logo.png" alt="ReiMultiBlog" className="mx-auto h-24 w-24 object-contain animate-fade-up" />
        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight animate-fade-up" style={{ animationDelay: "60ms" }}>
          <span className="brand-gradient">ReiMultiBlog</span>
        </h1>
        <p className="mt-5 text-lg text-muted-foreground max-w-2xl mx-auto animate-fade-up" style={{ animationDelay: "120ms" }}>
          {t(
            "Uma plataforma editorial que reúne três blogs independentes — guias práticos e resultados de experiências reais em vida na Suíça, desenvolvimento de software e automação.",
            "An editorial platform bringing together three independent blogs — practical guides and results from real experiments in Swiss life, software development and automation."
          )}
        </p>
      </section>

      {/* Mission / blogs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2">{t("A nossa missão", "Our mission")}</h2>
        <p className="text-muted-foreground max-w-2xl mb-8">{t("Partilhar conhecimento útil e prático em três áreas que combinam vida real e tecnologia.", "Share useful, practical knowledge across three areas blending real life and technology.")}</p>
        <div className="grid gap-6 lg:grid-cols-3">
          {(blogs.length ? blogs : Object.keys(BLOG_INFO).map((id) => ({ id }))).map((b) => (
            <div key={b.id} className="rounded-2xl border border-white/10 bg-card p-6 flex flex-col" style={{ borderTopColor: BLOG_INFO[b.id]?.color, borderTopWidth: 3 }}>
              <BlogBadge blog={b.id} />
              <h3 className="mt-3 font-heading text-xl font-bold">{lang === "pt" ? BLOG_INFO[b.id]?.pt : BLOG_INFO[b.id]?.en}</h3>
              <p className="mt-2 text-sm text-muted-foreground flex-1">{lang === "pt" ? DESC[b.id]?.pt : DESC[b.id]?.en}</p>
              <Link to={`/blog/${b.id}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: BLOG_INFO[b.id]?.color }}>
                {t("Visitar blog", "Visit blog")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8">{t("O que vais encontrar", "What you'll find")}</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => {
            const [title, body] = lang === "pt" ? s.pt : s.en;
            return (
              <div key={i} className="rounded-2xl border border-white/10 bg-card p-6">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/15 border border-primary/30 mb-4"><s.icon className="h-5 w-5 text-primary" /></span>
                <h3 className="font-heading font-bold">{title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}
